import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Slider } from "./ui/slider";
import { Loader2, X } from "lucide-react";
import CustomSelect from "./CustomSelect";
import CustomDatePicker from "./CustomDatePicker";

export default function TaskFormModal({ open, onClose, onSave, task, options = { systems: [], countries: [], priorities: [], statuses: [], projects: [] }, prefillInfo = "" }) {
  const [formData, setFormData] = useState({
    project: "",
    system: "",
    channel: "",
    macro: "",
    country: "ITA",
    priority: "MEDIUM",
    status: "TODO",
    info: "",
    deadline: null,
    completion: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        project: task.project || "",
        system: task.system || "",
        channel: task.channel || "",
        macro: task.macro || "",
        country: task.country || "ITA",
        priority: task.priority || "MEDIUM",
        status: task.status || "TODO",
        info: task.info || "",
        deadline: task.deadline || null,
        completion: task.completion || 0
      });
    } else {
      setFormData({
        project: "",
        system: "",
        channel: "",
        macro: "",
        country: "ITA",
        priority: "MEDIUM",
        status: "TODO",
        info: prefillInfo || "",  // Use voice command prefill
        deadline: null,
        completion: 0
      });
    }
  }, [task, open, prefillInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="task-form-modal-container">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/80 animate-in fade-in-0"
        onClick={onClose}
      />
      
      {/* Content */}
      <div className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] sm:max-w-[600px] max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] bg-card border border-border p-6 shadow-lg rounded-lg animate-in fade-in-0 zoom-in-95" data-testid="task-form-modal">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
          <h2 className="text-lg font-semibold leading-none tracking-tight">{task?.id ? "Modifica Task" : "Nuovo Task"}</h2>
          <p className="text-sm text-muted-foreground">Compila i campi per {task?.id ? "aggiornare il" : "creare un nuovo"} task</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Info - Main field */}
          <div className="space-y-2">
            <Label htmlFor="info" className="text-sm">Descrizione *</Label>
            <Textarea
              id="info"
              value={formData.info}
              onChange={(e) => handleChange("info", e.target.value)}
              placeholder="Descrizione del task..."
              className="bg-secondary resize-none text-sm"
              rows={2}
              required
              data-testid="task-info-input"
            />
          </div>

          {/* Row 1: Project & System */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="project" className="text-sm">Progetto</Label>
              <Input
                id="project"
                value={formData.project}
                onChange={(e) => handleChange("project", e.target.value)}
                placeholder="LJ, BMW, TME..."
                className="bg-secondary h-9 text-sm"
                data-testid="task-project-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="system" className="text-sm">Sistema</Label>
              <CustomSelect
                value={formData.system || ""}
                onValueChange={(v) => handleChange("system", v)}
                placeholder="Seleziona sistema"
                options={[
                  { value: "", label: "Seleziona sistema" },
                  ...options.systems.map(s => ({ value: s, label: s })),
                  { value: "Altro", label: "Altro" }
                ]}
                triggerClassName="bg-secondary h-9 text-sm"
              />
            </div>
          </div>

          {/* Row 2: Channel & Macro */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="channel" className="text-sm">Channel</Label>
              <Input
                id="channel"
                value={formData.channel}
                onChange={(e) => handleChange("channel", e.target.value)}
                placeholder="B2B, B2C, CC..."
                className="bg-secondary h-9 text-sm"
                data-testid="task-channel-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="macro" className="text-sm">Macro</Label>
              <Input
                id="macro"
                value={formData.macro}
                onChange={(e) => handleChange("macro", e.target.value)}
                placeholder="Categoria..."
                className="bg-secondary h-9 text-sm"
                data-testid="task-macro-input"
              />
            </div>
          </div>

          {/* Row 3: Country, Priority, Status */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Paese</Label>
              <CustomSelect
                value={formData.country}
                onValueChange={(v) => handleChange("country", v)}
                placeholder="Paese"
                options={options.countries.map(c => ({ value: c, label: c }))}
                triggerClassName="bg-secondary h-9 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Priorità</Label>
              <CustomSelect
                value={formData.priority}
                onValueChange={(v) => handleChange("priority", v)}
                placeholder="Priorità"
                options={[
                  { value: "HIGH", label: "HIGH" },
                  { value: "MEDIUM", label: "MEDIUM" },
                  { value: "LOW", label: "LOW" }
                ]}
                triggerClassName="bg-secondary h-9 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Status</Label>
              <CustomSelect
                value={formData.status}
                onValueChange={(v) => handleChange("status", v)}
                placeholder="Status"
                options={options.statuses.map(s => ({ value: s, label: s }))}
                triggerClassName="bg-secondary h-9 text-sm"
              />
            </div>
          </div>

          {/* Row 4: Deadline & Completion */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Deadline</Label>
              <CustomDatePicker
                value={formData.deadline}
                onChange={(date) => handleChange("deadline", date)}
                placeholder="Nessuna deadline"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Completamento: {Math.round(formData.completion * 100)}%</Label>
              <div className="pt-2">
                <Slider
                  value={[formData.completion * 100]}
                  onValueChange={([v]) => handleChange("completion", v / 100)}
                  max={100}
                  step={5}
                  data-testid="task-completion-slider"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto" data-testid="task-cancel-btn">
              Annulla
            </Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto" data-testid="task-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {task?.id ? "Salva" : "Crea"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
